import { Calendar, Download, ChevronRight, Github, Upload, MessageSquare } from 'lucide-react';
import { formatFileSize } from '@/features/teacher/activities/utils';
import { formatInstituteDate } from '@/features/teacher/utils';
import { shortWeekday } from '../../utils';
import { getSubjectStyle } from './homeworkTheme';
import type { StudentWorkItem } from './workItem';

interface HomeworkTaskCardProps {
  homework: StudentWorkItem;
  isFirst?: boolean;
  isOverdue?: boolean;
  isPastDate?: boolean;
  showKindLabel?: boolean;
  /** `key` of the work mid-upload, so only its own button says so. */
  uploadingKey: string | null;
  onDownloadWorksheet: (work: StudentWorkItem) => void;
  onViewDetails: (work: StudentWorkItem) => void;
  onOpenRepoModal: (work: StudentWorkItem) => void;
  onHandIn: (work: StudentWorkItem) => void;
}

export function HomeworkTaskCard({
  homework,
  isFirst = false,
  isOverdue = false,
  isPastDate = false,
  showKindLabel = false,
  uploadingKey,
  onDownloadWorksheet,
  onViewDetails,
  onOpenRepoModal,
  onHandIn,
}: HomeworkTaskCardProps) {
  const isUploading = uploadingKey === homework.key;
  const theme = getSubjectStyle(homework.subject.name);
  const feedback = homework.submission?.feedback ?? null;

  return (
    <div
      className={`rounded-2xl bg-white dark:bg-[#1b1d34] border ${
        isOverdue ? 'border-rose-200 dark:border-rose-900/40 hover:border-rose-400' : 'border-slate-200/80 dark:border-white/10 hover:border-indigo-500/30 dark:hover:border-indigo-400/30'
      } p-4 sm:px-5 sm:px-3 shadow-xs transition-all flex flex-col gap-2`}
    >
      {/* Top Row */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 w-full">
        {/* Left: Icon + Metadata + Title */}
        <div className="flex items-start gap-4 min-w-0 flex-1">
          <div className={`grid h-14 w-14 place-items-center rounded-xl shrink-0 ${theme.iconBg}`}>
            {theme.icon}
          </div>

          <div className="flex flex-col min-w-0 flex-1">
            {/* Subject, Teacher & Assigned Date */}
            <div className="flex flex-wrap items-center gap-1.5 text-xs font-bold text-slate-500 dark:text-slate-400">
              <span className={`uppercase tracking-wider ${theme.textAccent}`}>
                {homework.subject.name}
              </span>
              <span>•</span>
              <span className="text-slate-700 dark:text-slate-300 font-semibold">{homework.assignedBy}</span>
              <span>•</span>
              <span className="text-slate-500 dark:text-slate-400 font-medium">
                Assigned: {shortWeekday(homework.assignedOn)}, {formatInstituteDate(homework.assignedOn)}
              </span>
              {showKindLabel && (
                <span
                  className={
                    homework.kind === 'HOMEWORK'
                      ? 'font-bold text-blue-600 dark:text-blue-400'
                      : 'font-bold text-purple-600 dark:text-purple-400'
                  }
                >
                  {homework.kind === 'HOMEWORK' ? 'Homework' : 'Activity'}
                </span>
              )}
              {homework.typeLabel && (
                <span className="pill p-0">({homework.typeLabel})</span>
              )}
            </div>

            {/* Task Title */}
            <h4 className="text-base font-bold text-slate-900 dark:text-white mt-1 leading-snug">
              {homework.title}
            </h4>
          </div>
        </div>

        {/* Right: Submission Due Date & Status Badge */}
        <div className="flex items-center gap-3 self-end md:self-center shrink-0">
          {isOverdue ? (
            <div className="flex items-center gap-1.5 text-xs sm:text-sm font-bold text-rose-600 dark:text-rose-400">
              <Calendar className="h-3.5 w-3.5 text-rose-500" />
              <span>{shortWeekday(homework.dueDate)}, {formatInstituteDate(homework.dueDate)}</span>
            </div>
          ) : isFirst ? (
            <div className="flex flex-col items-end">
              <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 flex items-center gap-1">
                <Calendar className="h-3 w-3 text-slate-400" /> Submission Due
              </span>
              <span className="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-200">
                {shortWeekday(homework.dueDate)}, {formatInstituteDate(homework.dueDate)}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-300">
              <Calendar className="h-3.5 w-3.5 text-slate-400" />
              <span>{shortWeekday(homework.dueDate)}, {formatInstituteDate(homework.dueDate)}</span>
            </div>
          )}

          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold ${
              isOverdue
                ? 'bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-200 dark:border-rose-800/60'
                : 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/60'
            }`}
          >
            {isOverdue ? 'Overdue' : isPastDate ? 'Assigned' : 'Upcoming'}
          </span>
        </div>
      </div>

      {/* Teacher Remark Banner (if present) */}
      {feedback && (
        <div className="inline-flex items-center gap-2 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 text-xs font-bold text-emerald-800 dark:text-emerald-300 w-full">
          <MessageSquare className="h-4 w-4 text-emerald-600 shrink-0" />
          <span>
            <strong className="font-bold text-[12px] text-emerald-700 dark:text-emerald-400">Teacher Remark:</strong> "{feedback}"
          </span>
        </div>
      )}

      {/* Action Buttons Row */}
      <div className="flex flex-wrap items-center justify-end gap-2.5">
        {homework.attachment && (
          <button
            type="button"
            onClick={() => onDownloadWorksheet(homework)}
            title={`${homework.attachment.fileName}${
              homework.attachment.sizeBytes === null ? '' : ` · ${formatFileSize(homework.attachment.sizeBytes)}`
            }`}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-800/50 text-sm font-bold transition-all cursor-pointer"
          >
            <Download className="h-3.5 w-3.5" />
            <span>{homework.kind === 'ACTIVITY' ? 'Guidelines' : 'Worksheet'}</span>
          </button>
        )}

        <button
          type="button"
          onClick={() => onViewDetails(homework)}
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-slate-50 hover:bg-slate-100 dark:bg-[#1f2240] text-indigo-700 dark:text-indigo-300 border border-slate-200 dark:border-indigo-800/50 text-sm font-bold transition-all cursor-pointer"
        >
          <span>View Details</span>
          <ChevronRight className="h-3.5 w-3.5" />
        </button>

        {homework.submissionFormat === 'REPO_URL' ? (
          <button
            type="button"
            disabled={isUploading}
            onClick={() => onOpenRepoModal(homework)}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 dark:from-emerald-600 dark:to-teal-600 dark:hover:from-emerald-700 dark:hover:to-teal-700 text-white text-sm font-bold shadow-md shadow-emerald-500/20 dark:shadow-emerald-950/40 transition-all disabled:opacity-60 cursor-pointer"
          >
            <Github className="h-3.5 w-3.5" />
            <span>{homework.submission?.repoUrl ? 'Change repo link' : 'Submit repo link'}</span>
          </button>
        ) : (
          <button
            type="button"
            disabled={isUploading}
            onClick={() => onHandIn(homework)}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 dark:from-emerald-600 dark:to-teal-600 dark:hover:from-emerald-700 dark:hover:to-teal-700 text-white text-sm font-bold shadow-md shadow-emerald-500/20 dark:shadow-emerald-950/40 transition-all disabled:opacity-60 cursor-pointer"
          >
            <Upload className="h-3.5 w-3.5" />
            <span>{isUploading ? 'Uploading…' : homework.kind === 'ACTIVITY' ? 'Hand in work' : 'Upload Solution'}</span>
          </button>
        )}
      </div>
    </div>
  );
}
